// Plan data migrations (v0 → v4). The v4 image-element format is only read by
// planner v2. Pure functions, with no rendering dependencies.

export const CURRENT_VERSION = 4;
export const DEFAULT_OPACITY = 200;
export const DEFAULT_FILL_COLOR = 'rgba(114, 180, 95, 1)';
export const DEFAULT_STROKE_COLOR = 'rgba(0, 0, 0, 1)';
export const DEFAULT_LAYERS = [
  { id: 'Tall trees', name: 'Tall trees', opacity: 1, visible: true },
  { id: 'Trees', name: 'Trees', opacity: 1, visible: true },
  { id: 'Shrubs', name: 'Shrubs', opacity: 1, visible: true },
  { id: 'Herbs', name: 'Herbs', opacity: 1, visible: true },
  { id: 'Ground cover', name: 'Ground cover', opacity: 1, visible: true },
  { id: 'Roots', name: 'Roots', opacity: 1, visible: true },
  { id: 'Vines', name: 'Vines', opacity: 1, visible: true },
];

// --- v0 → v1: flatten plants + shapes into elements[] ----------------------
function migrateViewToV1(view = {}) {
  return view.zoomLevel === undefined
    ? {}
    : { scale: 1.2 ** view.zoomLevel, posX: view.posX, posY: view.posY };
}

function migratePlantsToV1(plants = []) {
  return plants.map((plant) => ({
    type: 'ellipse',
    data: {
      x: plant.x,
      y: plant.y,
      width: plant.size * 100,
      height: plant.size * 100,
      labels: plant.label && plant.label !== '' ? [plant.label] : [],
      labelX: 0,
      labelY: 0,
      fillColor: DEFAULT_FILL_COLOR,
      strokeColor: null,
      strokeWidth: null,
    },
    speciesIds: [plant.plant_id],
  }));
}

function migrateShapesToV1(shapes = []) {
  return shapes.map((shape) => {
    const label = shape.data.label;
    return {
      type: shape.type,
      data: Object.assign(
        shape.data,
        { fillColor: null, strokeColor: DEFAULT_STROKE_COLOR, strokeWidth: null },
        { labels: label && label !== '' ? [label] : [], labelX: 0, labelY: 0 },
      ),
      speciesIds: [],
    };
  });
}

function migrateToV1(currentData, data) {
  const newData = {
    name: currentData.name ? currentData.name : data.name,
    elements: migratePlantsToV1(currentData.plants?.length ? currentData.plants : data.plants)
      .concat(migrateShapesToV1(currentData.shapes?.length ? currentData.shapes : data.shapes)),
    settings: Object.keys(currentData.settings || {}).length ? currentData.settings : data.settings,
    view: migrateViewToV1(Object.keys(currentData.view || {}).length ? currentData.view : data.view),
    species: Object.keys(currentData.species || {}).length ? currentData.species : data.species,
    showMigrationMessage: true,
  };
  if (!newData.settings.name) newData.settings.name = newData.name;
  return newData;
}

// --- v1 → v2: structured labels --------------------------------------------
function migrateElementsToV2(elements = [], species = {}) {
  return elements.map((element) => {
    element.data.labels = element.data.labels.filter((l) => l).reduce((labels, label) => {
      const [speciesId, key] = element.speciesIds.reduce((acc, sid) => (
        ['name', 'scientific_name', 'parent_name'].reduce((acc2, k) => (
          species[sid] && species[sid][k] === label ? [sid, k] : acc2
        ), acc)
      ), [null, null]);
      if (speciesId) {
        const speciesLabel = labels.find((l) => l.type === 'species' && l.id === speciesId);
        if (speciesLabel) speciesLabel.keys.push(key);
        else labels.push({ type: 'species', id: speciesId, keys: [key] });
      } else {
        labels.push({ type: 'custom', value: label, visible: true });
      }
      return labels;
    }, []);
    element.speciesIds.forEach((speciesId) => {
      if (!element.data.labels.find((l) => l.type === 'species' && l.id === speciesId)) {
        element.data.labels.push({ type: 'species', id: speciesId, keys: [] });
      }
    });
    if (element.data.name) {
      element.data.labels.unshift({ type: 'custom', value: element.data.name, visible: false });
    }
    delete element.data.name;
    return element;
  });
}

function migrateToV2(currentData, data) {
  currentData = currentData.version === 1 ? currentData : migrateToV1(currentData, data);
  const newData = {
    name: currentData.name ? currentData.name : data.name,
    elements: currentData.elements?.length ? currentData.elements : data.elements,
    settings: Object.keys(currentData.settings || {}).length ? currentData.settings : data.settings,
    view: Object.keys(currentData.view || {}).length ? currentData.view : data.view,
    species: Object.keys(currentData.species || {}).length ? currentData.species : data.species,
    image: currentData.image || data.image,
    showMigrationMessage: true,
  };
  newData.elements = migrateElementsToV2(newData.elements, newData.species);
  return newData;
}

// --- v2 → v3: layers + per-element layer/date ------------------------------
function getLayerFromSpecies(speciesIds = [], species = {}) {
  for (const id of speciesIds) {
    const s = species[id];
    if (!s || !s['Layer']) continue;
    if (Array.isArray(s['Layer'])) { if (s['Layer'].length) return s['Layer'][0]; }
    else return s['Layer'];
  }
  return null;
}

function migrateElementsToV3(elements = [], species = {}) {
  elements.forEach((element) => {
    element.data.layer = getLayerFromSpecies(element.speciesIds, species);
    element.data.addedDate = null;
    element.data.removedDate = null;
  });
}

function migrateToV3(currentData, data) {
  currentData = currentData.version === 2 ? currentData : migrateToV2(currentData, data);
  currentData.layers = DEFAULT_LAYERS;
  migrateElementsToV3(currentData.elements, currentData.species);
  currentData.showMigrationMessage = true;
  return currentData;
}

// --- v3 → v4: background image becomes a locked image element --------------
function migrateToV4(currentData, data) {
  currentData = currentData.version === 3 ? currentData : migrateToV3(currentData, data);
  const image = currentData.image;
  const elements = currentData.elements || [];
  const imageElement = image?.id ? {
    type: 'image',
    speciesIds: [],
    data: {
      imageId: image.id,
      name: image.name || image.filename || 'Background image',
      url: image.url,
      x: image.x || 0,
      y: image.y || 0,
      width: image.width || null,
      height: image.height || null,
      rotation: image.rotation || 0,
      opacity: image.opacity ?? 200,
      visible: currentData.settings?.showImage !== false,
      locked: true,
      layer: null,
      addedDate: null,
      removedDate: null,
      labels: [],
      labelX: 0,
      labelY: 0,
    },
  } : null;

  const v4Data = { ...currentData };
  delete v4Data.image;
  return {
    ...v4Data,
    version: 4,
    elements: imageElement ? [imageElement, ...elements] : elements,
    // v3 → v4 is a lossless, automatic upgrade — only an older plan (flagged by
    // the pre-v3 migrations) earns the "made with an older planner" banner.
    showMigrationMessage: currentData.showMigrationMessage === true,
  };
}

function getCurrentData(data, storageData) {
  return storageData && storageData.timestamp && storageData.timestamp > data.timestamp
    ? storageData
    : data;
}

// Bring any plan payload up to the current version. Mirrors v1's DataMigrator.
export function migrate(data, storageData = null) {
  if (!data) return { version: CURRENT_VERSION, layers: DEFAULT_LAYERS, elements: [] };
  const currentData = getCurrentData(data, storageData);
  const plan = currentData.version === CURRENT_VERSION ? currentData : migrateToV4(currentData, data);
  ensureSpeciesLabels(plan.elements);
  return plan;
}

// Every plant on an element has its species label (v2 migration invariant) —
// one with no keys shows nothing but can be switched on in Properties.
function ensureSpeciesLabels(elements = []) {
  for (const el of elements || []) {
    if (!el?.data || !(el.speciesIds || []).length) continue;
    const labels = el.data.labels || (el.data.labels = []);
    for (const sid of el.speciesIds) {
      if (!labels.some((l) => l?.type === 'species' && l.id === sid)) labels.push({ type: 'species', id: sid, keys: [] });
    }
  }
}
